import {
  Box,
  Card,
  CircularProgress,
  Typography,
  useTheme,
} from '@mui/material';
import { Link } from 'react-router-dom';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';

import { useFeatureIsOn } from '@growthbook/growthbook-react';

import {
  useAsyncEffectWithLoading,
  useRiskReportDoc,
} from '../../_tabs/riskTab/RiskReportCard/helpers';

import { SolidButton } from './quick-actions/styles';

import { Show } from '../../../components/Show';
import { useCowbellTranslations } from '../../../i18n/translations';
import { toUniversalDate } from '../../../utils/date.utils';
import { FEATURE_FLAG_KEYS } from '../../../config/growthbook';
import {
  CARD_PADDING_REM,
  TILE_SUBTITLE_FONT_SIZE,
  TILE_SUBTITLE_GAP,
  rem,
} from '../../../components/panels/panelLayout';
import { getDashboardPanelTokens } from './dashboardPanelTokens';
import { RiskFindingsSummary } from './RiskFindingsSummary';
import { RiskFindingsUnavailable } from './RiskFindingsUnavailable';
import { MissingDomainNotice } from './MissingDomainNotice';
import { useRiskFindings } from './useRiskFindings';
import { TileHeading } from './TileHeading';

const ALL_FINDINGS_PATH = '/customer/insights';

type DashboardRiskReportCardProps = { accountId: string };

/**
 * The dashboard's risk report tile, in the same visual language as the factors
 * and activity tiles: one heading weight, supporting copy a step lighter, and
 * the actions pinned to the bottom of the card.
 *
 * The tile fills its row and pins its actions to the bottom, so whatever slack
 * the taller column beside it creates is distributed rather than left as a gap
 * under the card. The empty state is the widest that gap gets: once the account
 * has findings, the summary fills most of the height on its own.
 */
const DashboardRiskReportCard = (props: DashboardRiskReportCardProps) => {
  const { t } = useCowbellTranslations();
  const { palette } = useTheme();
  const theme = useTheme();
  const tokens = getDashboardPanelTokens(palette.mode);

  const showRiskReportDownload = useFeatureIsOn(
    FEATURE_FLAG_KEYS.SHOW_RISK_REPORT_DOWNLOAD
  );
  const riskReportDoc = useRiskReportDoc({ accountId: props.accountId });

  const reportGeneration = useAsyncEffectWithLoading({
    effect: riskReportDoc.methods.generateReport,
  });

  const {
    findings,
    isUnavailable: findingsUnavailable,
    isLoaded: findingsLoaded,
  } = useRiskFindings(props.accountId);

  const hasReport = Boolean(riskReportDoc.data.latestReportDate);
  // The unavailable copy tells the reader the document is complete and safe to
  // download. It may only be shown where that is true and the download is
  // actually offered -- not when the report has yet to be generated, the
  // account has no domain, or downloads are switched off entirely.
  const canDownloadReport =
    hasReport &&
    Boolean(riskReportDoc.data.accountHasDomain) &&
    showRiskReportDownload;
  const formattedReportDate = toUniversalDate(
    riskReportDoc.data.latestReportDate
  );

  return (
    <Card
      data-qa="RISK_REPORT_PANEL"
      elevation={6}
      sx={{
        padding: CARD_PADDING_REM,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        display="flex"
        alignItems="baseline"
        justifyContent="space-between"
        gap={rem(12)}
      >
        <TileHeading>Cyber Risk Report</TileHeading>
        <Show when={hasReport}>
          <Typography
            sx={{
              flexShrink: 0,
              fontSize: rem(11.5),
              color: tokens.inkFaint,
              whiteSpace: 'nowrap',
            }}
          >
            {formattedReportDate}
          </Typography>
        </Show>
      </Box>

      <Show
        when={!riskReportDoc.isRiskScannerRunning}
        fallback={
          <Description tokens={tokens}>
            Once the risk scan of your account is complete, you will be able to
            access your latest Risk Report. We will send you an email
            notification as soon as the scan is finished.
          </Description>
        }
      >
        <Show
          when={findingsLoaded}
          fallback={
            <Show
              when={findingsUnavailable && canDownloadReport}
              fallback={
                <Description tokens={tokens}>
                  This Cyber Risk Report is an overall evaluation of your{' '}
                  {t('organization').toLowerCase()}&apos;s risk profile
                  presenting {t('individualized').toLowerCase()} findings with{' '}
                  {t('personalized').toLowerCase()} recommendations for
                  remediation.
                </Description>
              }
            >
              <RiskFindingsUnavailable />
            </Show>
          }
        >
          {/* Zero is an answer, not an absence: an account that has remediated
              everything reads "0 open findings to remediate". */}
          <RiskFindingsSummary
            findings={findings}
            reportAvailable={canDownloadReport}
          />
        </Show>
      </Show>

      <Box sx={{ marginTop: 'auto', paddingTop: rem(16) }}>
        <Show
          when={riskReportDoc.data.accountHasDomain && showRiskReportDownload}
        >
          <Show when={!hasReport}>
            <Typography
              sx={{
                marginBottom: rem(10),
                fontSize: rem(12),
                color: tokens.inkFaint,
              }}
            >
              No report available yet.
            </Typography>
          </Show>

          <Box display="flex" alignItems="center" gap={rem(12)} flexWrap="wrap">
            <Show when={hasReport}>
              <SolidButton
                onClick={riskReportDoc.methods.downloadReport}
                disabled={riskReportDoc.isDownloadDisabled}
                startIcon={<FileDownloadOutlinedIcon />}
                sx={{
                  // Sentence case, against the button's uppercase default:
                  // this is a label the reader reads, not a shout.
                  textTransform: 'none',
                  letterSpacing: 0,
                  // The icon wrapper carries its own line box on top of the
                  // glyph's size, which made this button 48px against the 40px
                  // of every other primary button on the dashboard. Zeroing the
                  // wrapper's line-height lets the button's own text set the
                  // height, as it does on a button with no icon.
                  '& .MuiButton-startIcon': {
                    lineHeight: 0,
                    marginLeft: 0,
                    marginRight: rem(6),
                  },
                  '& .MuiButton-startIcon > *:first-of-type': {
                    fontSize: rem(16),
                  },
                }}
              >
                Download report
              </SolidButton>
            </Show>

            <Box
              component="button"
              type="button"
              data-qa="RISK_REPORT_GENERATE"
              onClick={reportGeneration.request}
              disabled={
                reportGeneration.isLoading || riskReportDoc.isGenerationDisabled
              }
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: rem(6),
                marginLeft: 'auto',
                border: 'none',
                background: 'none',
                padding: 0,
                fontSize: rem(13),
                fontWeight: 600,
                color: tokens.inkFaint,
                cursor: 'pointer',
                '&:disabled': { color: tokens.inkFaint, cursor: 'default' },
                '&:hover:not(:disabled)': { textDecoration: 'underline' },
              }}
            >
              <Show
                when={reportGeneration.isLoading}
                fallback={<AutorenewIcon sx={{ fontSize: rem(15) }} />}
              >
                <CircularProgress size={14} color="inherit" />
              </Show>
              {riskReportDoc.isReportGenerating || reportGeneration.isLoading
                ? 'Generating…'
                : getGenerateLabel(hasReport)}
            </Box>

            <Show when={findings.total > 0 && findingsLoaded}>
              <Typography
                component={Link}
                to={ALL_FINDINGS_PATH}
                sx={{
                  fontSize: rem(13),
                  fontWeight: 600,
                  color: tokens.brand,
                  textDecoration: 'none',
                  whiteSpace: 'nowrap',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                {`All ${findings.total} findings \u2192`}
              </Typography>
            </Show>
          </Box>
        </Show>

        <Show
          when={!riskReportDoc.data.accountHasDomain && showRiskReportDownload}
        >
          <MissingDomainNotice
            onAddDomain={riskReportDoc.methods.redirectToAddDomain}
            action={
              <SolidButton
                onClick={riskReportDoc.methods.redirectToAddDomain}
                sx={{ textTransform: 'none', letterSpacing: 0 }}
              >
                Add domain
              </SolidButton>
            }
          />
        </Show>
      </Box>
    </Card>
  );
};

/** "Generate" the first time, "Regenerate" once a report already exists. */
const getGenerateLabel = (hasReport: boolean) =>
  hasReport ? 'Regenerate' : 'Generate report';

function Description({
  tokens,
  children,
}: Readonly<{ tokens: { inkFaint: string }; children: React.ReactNode }>) {
  return (
    <Typography
      sx={{
        marginTop: TILE_SUBTITLE_GAP,
        fontSize: TILE_SUBTITLE_FONT_SIZE,
        lineHeight: 1.55,
        color: tokens.inkFaint,
      }}
    >
      {children}
    </Typography>
  );
}

export default DashboardRiskReportCard;
